// src/components/common/AppBar/AppBar.jsx
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  AppBar as MuiAppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout,
  Settings,
} from '@mui/icons-material';
import { toggleSidebar } from '../../../store/slices/uiSlice';
import { useAuth } from '../../../hooks/useAuth';
import { ROUTES, USER_ROLES } from '../../../utils/constants';
import ThemeToggle from '../ThemeToggle';

const AppBar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, role, logout } = useAuth();
  const { sidebarPermanent } = useSelector((state) => state.ui);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleMenuToggle = () => {
    dispatch(toggleSidebar());
  };

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileClose();
    logout();
  };

  const handleProfile = () => {
    handleProfileClose();
    navigate(ROUTES.PROFILE);
  };

  const handleSettings = () => {
    handleProfileClose();
    navigate(ROUTES.ADMIN_SETTINGS);
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <MuiAppBar
      position="fixed"
      elevation={1}
      sx={{
        width: {
          md: sidebarPermanent ? 'calc(100% - 280px)' : '100%',
        },
        ml: {
          md: sidebarPermanent ? '280px' : 0,
        },

        bgcolor: 'background.paper',
        color: 'text.primary',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        {/* Left side - Menu button and title */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            onClick={handleMenuToggle}
            edge="start"
            sx={{
              mr: 2,
              display: {
                md: sidebarPermanent ? 'none' : 'block',
              },
              minHeight: 80,
              minWidth: 80,
              
            }}
          >
            <MenuIcon sx={{ fontSize: '2.5rem' }} />
          </IconButton>
          
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              display: { xs: 'none', sm: 'block' },
              fontWeight: 600,
            }}
          >
            Dashboard
          </Typography>
        </Box>

        {/* Right side - Theme toggle and user menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Theme toggle - only show on desktop when sidebar is permanent */}
          {!isMobile && !sidebarPermanent && <ThemeToggle />}
          
          {/* User role chip */}
          {role && (
            <Chip
              label={role.charAt(0).toUpperCase() + role.slice(1)}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ display: { xs: 'none', sm: 'block' } }}
            />
          )}

          {/* User avatar and menu */}
          <IconButton
            onClick={handleProfileClick}
            size="small"
            sx={{
              minHeight: 60,
              minWidth: 60,
            }}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'primary.main',
                fontSize: '1.4rem',
                
              }}
            >
              {getInitials(user?.username || user?.name)}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleProfileClose}
            onClick={handleProfileClose}
            PaperProps={{
              elevation: 3,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.12))',
                mt: 1.5,
                minWidth: 200,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {/* User info */}
            <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {user?.username || user?.name || 'User'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>

            <MenuItem onClick={handleProfile}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>

            {role === USER_ROLES.SUPER_ADMIN && (
              <MenuItem onClick={handleSettings}>
                <ListItemIcon>
                  <Settings fontSize="small" />
                </ListItemIcon>
                <ListItemText>Settings</ListItemText>
              </MenuItem>
            )}

            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </MuiAppBar>
  );
};

export default AppBar;